import {useEffect, useState} from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Image from 'react-bootstrap/Image';
import {useNavigate, useParams} from "react-router-dom";
import Heading3 from "../../Heading3";
import httpService from "../../../services/httpService";

const StepIntro = ({ onStageChange, hasTestingClub }) => {
    const { step, id } = useParams();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [loading, setLoading] = useState(false);
    const [podcastClub, setPodcastClub] = useState({});
    const [errorMessage, setErrorMessage] = useState('');
    const [isWizardCompleted, setIsWizardCompleted] = useState(false);

    useEffect(() => {
        setLoading(true);
        httpService.get('/dash/wizard/start/')
            .then((wizardRes) => {
                setPodcastClub({...wizardRes.data.podcast_club});
                setIsWizardCompleted(wizardRes.data.is_wizard_completed);
            })
            .catch((err) => {
                setErrorMessage(err.response?.data?.message || "Error loading data");
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (hasTestingClub) {
            goToNextStep();
        }
    }, [hasTestingClub]);

    const sections = [
        {
            id: 1,
            title: "Create New Podcast Club",
            description: "Start a new podcast club from scratch. Set up club details, add episodes, and invite listeners.",
            buttonText: "Start New Club",
            content: "Creating a new podcast club allows you to build a community around your audio content. You'll be able to customize your club's appearance, select episodes from your RSS feed, define practice skills for listeners to develop, and invite members to join your growing community."
        },
        {
            id: 2,
            title: "Manage Episodes",
            description: "Add or remove episodes from your RSS feed. Schedule episode releases and manage content.",
            buttonText: "Manage Episodes",
            content: "Episode management gives you complete control over your club's content library. You can add new episodes as they're published to your RSS feed, remove outdated content, organize episodes by topic or skill level, and schedule releases to maintain a consistent flow of content for your club members."
        },
        {
            id: 3,
            title: "View Analytics",
            description: "Track listener engagement, practice completion rates, and club performance metrics.",
            buttonText: "View Analytics",
            content: "Analytics provide valuable insights into how your club members are engaging with your content. Monitor completion rates for practice skills, track listening patterns across different episodes, measure overall club growth, and identify which content resonates most with your audience to inform future episode planning."
        },
        {
            id: 4,
            title: "Edit Existing Club",
            description: "Modify club settings, update descriptions, or change practice skills for your existing clubs.",
            buttonText: "Edit Clubs",
            content: "Editing your existing clubs allows you to keep your content fresh and relevant. Update club descriptions as your focus evolves, modify practice skills to better align with listener needs, adjust visibility settings, and refine your club's branding to ensure it continues to attract and engage the right audience."
        },
        {
            id: 5,
            title: "Resources & Help",
            description: "Access documentation, best practices, and guides to make the most of your podcast clubs.",
            buttonText: "View Resources",
            content: "Our comprehensive resource library is designed to help you succeed as a podcast club creator. Access step-by-step tutorials, learn best practices for engaging your audience, discover effective ways to structure practice skills, and find answers to common questions to maximize the impact of your podcast clubs."
        },
        {
            id: 6,
            title: "Community Guidelines",
            description: "Review guidelines for creating engaging content and maintaining a positive club environment.",
            buttonText: "Read Guidelines",
            content: "Community guidelines ensure that podcast clubs remain welcoming and productive spaces for all members. Learn about content standards, understand moderation best practices, discover how to foster meaningful discussions around your episodes, and create an environment where listeners feel comfortable practicing new skills and engaging with your content."
        }
    ];

    const handleSectionClick = (section) => {
        setSelectedSection(section);
        setShowModal(true);
    };

    const goToClubDashboard = () => {
        navigate('/podcaster-dashboard');
    };

    const goToNextStep = () => {
        onStageChange(1);
    };

    return (
        <div className="py-0">
            <Row className="mb-3">
                <Col sm={12}>
                    {/*<Heading3>Podcast Club Wizard Guides</Heading3>*/}
                    <p className="m-0">
                        Welcome to the Podcast Club wizard area. Choose an option below to read more information.
                    </p>
                </Col>
            </Row>

            <Row className="mb-0">
                {sections.map((section) => (
                    <Col md={6} className="mb-3" key={section.id}>
                        <Card className="h-100">
                            <Card.Body>
                                <Card.Title>{section.title}</Card.Title>
                                <Card.Text>{section.description}</Card.Text>
                                <Button
                                    variant={"primary"}
                                    onClick={() => handleSectionClick(section)}
                                >
                                    {section.buttonText}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Row className="mb-3" style={{ justifyContent: "center" }}>
                <Col sm={4}>
                    <label className={'mb-2'}>{hasTestingClub ? 'Go to your dashboard' : 'Choose between sample or new Club'}</label>
                    <button
                        className={"btn btn-light btn-block w-100"}
                        onClick={() => hasTestingClub ? navigate('/podcaster-dashboard') : onStageChange('choice', id)}
                    >
                        {hasTestingClub ? 'Go to your dashboard' : 'Back'}
                    </button>
                </Col>
                <Col sm={4}>
                    {isWizardCompleted ? (
                        <>
                            <label className={'mb-2'}>Go to dashboard</label>
                            <Button
                                variant={'primary'}
                                className={'w-100'}
                                onClick={goToClubDashboard}>
                                Go to club dashboard
                            </Button>
                        </>
                    ):''}
                </Col>
                <Col sm={4}>
                    <label className={'mb-2'}>Continue to next step</label>
                    <Button
                        variant={isWizardCompleted ? 'light' : 'primary'}
                        className={'w-100'}
                        onClick={goToNextStep}>
                        Start set up
                    </Button>
                </Col>
            </Row>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedSection?.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">{selectedSection?.description}</p>
                    <p>{selectedSection?.content}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StepIntro;